import { z } from 'zod';

import { boolParam, listQuerySchema } from './common.js';
import {
  connectionStatusSchema,
  entityStatusSchema,
  type AdPlatform,
  type ConnectionStatus,
} from './enums.js';

/**
 * INTEGRATIONS — platform bağlama, jeton kasası, senkron.
 *
 * Tasarım §4.1 (hesap bağlama), §5.3.2 (adapter deseni), §9 (hata yönetimi),
 * §10 (jeton kasası), §11 (Meta kısıtları).
 *
 * ---------------------------------------------------------------------------
 * Adapter sınırı — bu alanın varlık sebebi
 * ---------------------------------------------------------------------------
 * TÜM platform farklılıkları `AdPlatformConnector` arayüzünde ölür. Üst
 * katmanlar (analytics, automation, agent, creative) tek bir platform tipi
 * GÖRMEZ; yalnızca aşağıdaki normalize şekilleri görür. Yeni bir platform
 * eklemek = bir adapter yazmak, başka hiçbir dosyaya dokunmamak.
 *
 * Bu turda YALNIZCA Meta adapter'ı yazılıyor (§13 Meta-first). Google ve
 * TikTok için `NotImplementedConnector` var: uçlar 500 değil, tipli
 * `PLATFORM_NOT_AVAILABLE` döner ki arayüz kartı "yakında" diye göstersin.
 * Bağlanamayan bir düğmeye bastırmak, düğmeyi hiç koymamaktan kötüdür.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Jetonlar
 * ---------------------------------------------------------------------------
 * `accessTokenEnc` / `refreshTokenEnc` AES-256-GCM ile şifreli saklanır
 * (`TOKEN_VAULT_KEY`, 64 hex). Şifreli değer SÜRÜM ÖNEKLİ yazılır
 * (`v1:<iv>:<tag>:<ciphertext>`) ki anahtar rotasyonu ileride mümkün olsun.
 *
 * Çözülmüş jeton HİÇBİR DTO'ya, HİÇBİR loga, HİÇBİR hata mesajına girmez.
 * `lastError` platformdan gelen ham metni taşıyor ve içinde jeton parçası
 * olabilir — arayüze verilmeden önce temizlenir (`sanitizeProviderError`).
 */

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 OAuth geri dönüş adresi — Meta konsoluna EKLENMESİ ZORUNLU
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Meta uygulamasının **Valid OAuth Redirect URIs** listesine BİREBİR şu adres
 * eklenmeden hiçbir bağlantı akışı çalışmaz (Meta tam eşleşme arar; sondaki
 * `/` bile fark eder):
 *
 *     https://api.mentorivo.com/integrations/meta/callback
 *
 * API'de global `api` öneki YOK — uçlar kökten. Adres `API_PUBLIC_URL` +
 * `OAUTH_CALLBACK_PATH` olarak üretilir; elle ikinci kez yazılmaz.
 */
export const OAUTH_CALLBACK_PATH = (platform: string) => `/integrations/${platform}/callback`;

/// OAuth `state` kaydının ömrü. CSRF koruması bu; state çereze yazılmaz,
/// Redis'te tutulur (workspaceId + userId + nonce + dönüş rotası).
export const OAUTH_STATE_TTL_SECONDS = 10 * 60;

/**
 * Meta izin kapsamları. `config_id` (Facebook Login for Business) varsa
 * izin ekranı yapılandırmadan gelir; scope yine de gönderilir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `leads_retrieval` (Faz 7) — MEVCUT BAĞLANTILAR YENİDEN ONAY İSTER
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu listeye yeni bir kapsam eklemek, ZATEN BAĞLI hesapların jetonunu
 * geçersiz kılmıyor — eski jeton eski kapsamlarla çalışmaya devam ediyor.
 * Ama o jetonda `leads_retrieval` YOK ve Meta lead uçlarını izinsiz jetona
 * kapatıyor: kullanıcı yeniden bağlanana kadar lead senkronu o hesapta
 * çalışmaz.
 *
 * Bu sessiz kalmıyor: `AdConnection.scopes` platformun GERÇEKTEN verdiği
 * kapsamları saklıyor (`ConnectorTokenResult.scopes`) ve lead senkronu önce
 * ona bakıp `LEADS_SCOPE_MISSING` diye raporluyor — boş bir lead listesi
 * dönüp "hiç lead yok" demiyor.
 *
 * ⚠️ Kapsamın kendisi App Review'a da tabi: geliştirme modunda uygulamanın
 * yöneticileri/test kullanıcıları için çalışıyor, herkese açık kullanım için
 * Meta onayı gerekiyor (`ads_management` ile aynı durum).
 */
export const META_SCOPES = [
  'ads_management',
  'ads_read',
  'business_management',
  'leads_retrieval',
] as const;

/// Lead uçları için zorunlu kapsam. Ayrı bir sabit çünkü kontrol eden taraf
/// (`lead-sync.ts`) tüm listeyi değil YALNIZ bunu soruyor — liste büyüdüğünde
/// kontrolün de büyümesi gerekmesin.
export const META_LEADS_SCOPE = 'leads_retrieval';

/// Sayfa listesi için gereken kapsam. ⚠️ `META_SCOPES`te YOK — bağlantı
/// `META_CONFIG_ID` (Facebook Login for Business) üzerinden kurulduğu için
/// izinleri Meta tarafındaki yapılandırma belirliyor, bizim `scope`
/// parametremiz değil. Ölçüldü (25 Ağu 2026): canlı bağlantı bunu TAŞIYOR,
/// `leads_retrieval` taşımıyor — yani liste ile gerçek ayrışabiliyor ve
/// çağıran taraf `AdConnection.scopes`a bakmak zorunda.
export const META_PAGES_SCOPE = 'pages_show_list';

/**
 * Reklamın yayınlandığı marka kimliği (Meta'da Facebook Sayfası).
 *
 * `identityRef` alanına giren değer budur. Kullanıcıya URL'den kimlik
 * kopyalatmak yerine listelemek için var: 25 Ağu 2026'da kullanıcı
 * `facebook.com/profile.php?id=…` adresinden bir kimlik yapıştırdı ve Meta
 * kreatifi "Facebook Sayfası Eksik" (subcode 1443121) ile reddetti — o kimlik
 * bir Sayfa değildi ve bunu ancak reklam oluşturulurken öğrenebildik.
 */
export interface PlatformIdentity {
  id: string;
  name: string;
  /// Sayfanın herkese açık adresi — kullanıcı doğru sayfayı seçtiğini görsün.
  link: string | null;
}

export interface IdentityListResponse {
  identities: PlatformIdentity[];
  /// Bağlantı bu listeyi okuyabiliyor mu. `false` ise `identities` boş ve
  /// sebep `reasonKey`te — boş liste "sayfanız yok" diye okunmasın.
  supported: boolean;
  reasonKey: string | null;
}

/**
 * Google Ads izin kapsamı — reklam için bir, kimlik için bir.
 *
 * Google, Meta'nın aksine okuma ve yazmayı ayırmıyor: `adwords` kapsamı hem
 * raporu hem mutasyonu veriyor. "Yalnız okuma izni isteyelim" seçeneği YOK;
 * kullanıcıya gösterilen izin ekranı kaçınılmaz olarak yazma da istiyor.
 *
 * 🔴 `openid` SÜS DEĞİL, bağlantının kurulma şartı. `platformUserId` alanını
 * `tokeninfo` ucunun `sub` değerinden okuyoruz (`google.connector.ts` →
 * `describeTokens`) ve `sub` bir OpenID alanı: Google onu yalnız OpenID
 * kapsamı verilmiş jetonlar için döndürüyor. Yalnız `adwords` istendiğinde
 * `tokeninfo` 200 dönüyor ama `sub` GELMİYOR, `describeTokens` de `auth`
 * hatası fırlatıyor — yani bağlantı HER denemede aynı yerde ölüyor.
 * 14 Ağu 2026'da tam olarak bu yaşandı: kod takası başarılıydı, `AdConnection`
 * satırı yine de hiç açılmadı ve arayüz "Bu platformda bağlantı yok" dedi.
 * Kapsamı buradan çıkarırsan Google entegrasyonu sessizce kırılır.
 *
 * `openid` TEK BAŞINA yalnız kararlı bir hesap kimliği veriyor — ad, e-posta
 * ya da fotoğraf İSTEMİYORUZ (`profile`/`email` bilinçli olarak yok), bu
 * yüzden `displayName` hâlâ null. Google'ın sınıflandırmasında `openid`
 * hassas olmayan bir kapsam; ek doğrulama incelemesi gerektirmiyor.
 *
 * ⚠️ `offline` erişim ve `prompt=consent` ZORUNLU — Google refresh token'ı
 * yalnız ilk onayda, yalnız bu iki parametreyle veriyor. İkinci kez bağlanan
 * bir kullanıcı bunlar olmadan yalnız 1 saatlik access token alır ve bağlantı
 * ertesi gün sessizce ölür.
 */
export const GOOGLE_ADS_SCOPES = ['openid', 'https://www.googleapis.com/auth/adwords'] as const;

// ═══════════════════════════════════════════════════════════════════════════
// Bağlantı sağlığı — eşikler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * §9 healthy/degraded/disconnected diyor ama eşik VERMİYOR. Rozetin bir anlamı
 * olması için eşikler burada, tek yerde:
 *
 *   HEALTHY      → son senkron başarılı VE jeton geçerli
 *   DEGRADED     → üst üste ≥3 senkron hatası VEYA son BAŞARILI senkron
 *                  2 saatten eski (jeton hâlâ geçerli)
 *   DISCONNECTED → jeton yenileme başarısız (invalid_grant / OAuthException 190)
 *
 * DEGRADED otomasyonu DURDURMAZ; DISCONNECTED durdurur (§9 güvenli duraklatma).
 * Aradaki fark önemli: geçici bir API hatası yüzünden kullanıcının kurallarını
 * kapatmak, kopuk bağlantı yüzünden kapatmakla aynı şey değil.
 */
export const HEALTH_THRESHOLD = {
  consecutiveFailuresForDegraded: 3,
  staleSyncMinutesForDegraded: 120,
} as const;

/// Elle senkron sıklık sınırı — hesap başına. Platform kotasını yakmamak için.
export const MANUAL_SYNC_MIN_INTERVAL_SECONDS = 15 * 60;

/// Hesap keşfi önbelleği: ekran her açılışta platform kotası yakmasın.
export const DISCOVERY_CACHE_SECONDS = 10 * 60;

// ═══════════════════════════════════════════════════════════════════════════
// Normalize adapter tipleri — üst katmanların gördüğü TEK şekil
// ═══════════════════════════════════════════════════════════════════════════

/// Adapter'ın neyi destekleyip neyi desteklemediği. Arayüz düğmeyi buna göre
/// gösterir; "destekleniyor mu" sorusunun cevabı koda gömülü olmaz.
export interface ConnectorCapabilities {
  platform: AdPlatform;
  /// Gerçek implementasyon var mı. GOOGLE/TIKTOK bugün `false`.
  available: boolean;
  /// `available: false` ise nedeni — i18n anahtarı, düz metin değil.
  unavailableReasonKey: string | null;
  canReadInsights: boolean;
  canSetStatus: boolean;
  /// Platformda `ARCHIVED` diye YAZILABİLİR bir durum var mı. Meta ve
  /// LinkedIn'de var. Google Ads (ENABLED/PAUSED/REMOVED), TikTok ve X'te YOK
  /// ve bir gün `true` olmayacak: oralarda "listeden kaldır"ın tek karşılığı
  /// geri alınamaz silme, ve onu arşivleme düğmesinin arkasına saklamıyoruz.
  canArchive: boolean;
  canSetBudget: boolean;
  canPublishCreative: boolean;
  /**
   * Sıfırdan kampanya/ad set/reklam açabiliyor mu (RMF — Faz 2 sözleşmesi).
   *
   * Ayrı bir bayrak, `canPublishCreative`in içine katılmadı: kreatif
   * yayınlamak MEVCUT bir ad set'in altına reklam koymak; RMF ise hiyerarşiyi
   * baştan kurmak. İkincisi hedefleme, bütçe, faturalama olayı ve optimizasyon
   * hedefi ister — biri destekleniyor diye diğeri desteklenmiyor.
   *
   * 🔴 Google Ads **Standard Access** başvurusunun ön koşulu bu yetenek
   * (tasarım §11): başvuru "uygulamanız kampanya oluşturabiliyor mu"
   * sorusuna evet cevabı istiyor.
   */
  canCreateEntities: boolean;
  /**
   * Anahtar kelime okuyup yazabiliyor mu (Faz 5).
   *
   * 🔴 Bu bayrak bir YETENEK değil, bir KAVRAM sorusu. Meta'da anahtar kelime
   * DİYE BİR ŞEY YOK — hedefleme ilgi alanı ve kitle üzerinden çalışıyor.
   * Yani `false` dönmesi "Meta adapter'ı henüz yazılmadı" demek değil, "bu
   * platformda sorunun karşılığı yok" demek; hiçbir zaman `true` olmayacak.
   *
   * Ayrımın pratik sonucu: kelime aksiyonu taşıyan bir kural Meta hesabına
   * bağlıysa bu, düzeltilecek bir eksiklik değil kullanıcıya söylenecek bir
   * gerçek. Motor onu `SKIPPED` + `PLATFORM_HAS_NO_KEYWORDS` ile geçiyor,
   * `FAILED` saymıyor — başarısız olan bir şey yok.
   */
  canManageKeywords: boolean;
  /**
   * Lead formu gönderimlerini okuyabiliyor mu (Faz 7).
   *
   * ⚠️ `canManageKeywords` gibi bir KAVRAM sorusu DEĞİL, bir yetenek sorusu:
   * üç platformun üçünde de lead formu var. `false` dönmesi "bu platformda
   * lead diye bir şey yok" demiyor, "bizim adapter'ımız henüz okumuyor"
   * diyor — Google için doğrusu bu ve arayüz "yakında" diyebilir.
   *
   * Bu ayrım pratik: kelime aksiyonu Meta'da bir HATA değil, lead okumanın
   * Google'da olmaması bir EKSİK. İkisini aynı bayrağa koymak, kapatılacak
   * bir borcu kapatılmayacak bir gerçek gibi göstermek olurdu.
   */
  canReadLeads: boolean;
  /// Desteklenen metrik seviyeleri (Google Ads'te AD seviyesi farklı çalışır).
  levels: ('ACCOUNT' | 'CAMPAIGN' | 'ADSET' | 'AD')[];
}

/// Platformdan keşfedilen reklam hesabı (henüz bağlanmamış olabilir).
export interface DiscoveredAccount {
  platformRef: string;
  name: string;
  currency: string;
  timezone: string;
  /// Platformun kendi durum metni (`active`, `disabled`, `unsettled`…).
  status: string;
  /// Bu workspace'te zaten bağlı mı — HK3: işaretli ve seçilemez.
  alreadyConnected: boolean;
}

/// Adapter'ın döndürdüğü normalize hiyerarşi düğümü. Senkron bunu upsert eder.
export interface NormalizedEntity {
  level: 'CAMPAIGN' | 'ADSET' | 'AD';
  platformRef: string;
  parentPlatformRef: string | null;
  name: string;
  status: z.infer<typeof entityStatusSchema>;
  objective?: string | null;
  dailyBudget?: number | null;
  lifetimeBudget?: number | null;
  startTime?: string | null;
  stopTime?: string | null;
  /**
   * Platformdaki KREATİFİN kendisi — yalnız `level: 'AD'` satırlarında dolu.
   *
   * Faz 6'da eklendi. Kolonlar (`ads.headline` / `.bodyText` / `.ctaType` /
   * `.previewUrl`) Faz 1'den beri VARDI ama senkron hiçbirini yazmıyordu;
   * yalnız bizim yayınladığımız reklamlarda doluydular. Sonuç: kreatif
   * analiz ekranı, kullanıcının reklamlarının çoğunu metinsiz ve görselsiz
   * listeliyordu — yani tam olarak "hangi kreatifim yoruldu" sorusunun
   * sorulduğu yerde.
   *
   * `undefined` = adapter bu bilgiyi vermiyor; `null` = verdi ama boş.
   * Ayrım yazma tarafında ÖNEMLİ: `undefined` alanı ezmiyor, `null` eziyor.
   */
  creative?: {
    headline?: string | null;
    bodyText?: string | null;
    ctaType?: string | null;
    previewUrl?: string | null;
  };
}

/**
 * Adapter'ın döndürdüğü normalize metrik satırı.
 *
 * ⚠️ `ctr/cpc/cpm/roas` burada DOLDURULMAZ diye bir kural yok, ama toplamlarda
 * ASLA kullanılmaz — `analytics.ts`'teki türetilmiş metrik kuralına bak.
 * Satır düzeyinde sıralama/filtre için saklanıyorlar.
 */
export interface NormalizedInsight {
  level: 'ACCOUNT' | 'CAMPAIGN' | 'ADSET' | 'AD';
  /// Platformun kimliği; senkron bunu bizim `entityId`'mize çevirir.
  platformRef: string;
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  /**
   * Tekil kişi sayısı ve gösterim/erişim — Faz 2.
   *
   * `null` = platform bu satırda vermedi. ⚠️ `0` ile karıştırılmamalı:
   * erişimi olmayan bir gün ile erişimi bilinmeyen bir gün farklı şeyler ve
   * ikincisine 0 yazmak frekansı sonsuza gönderir.
   *
   * Satır GÜN BAZLI olduğu için buradaki değerler gerçek. Toplamda
   * kullanılamazlar — `analytics.ts`'teki `aggregatableOver: 'singleDay'`.
   */
  reach: number | null;
  frequency: number | null;
  /// Hesabın para birimi. `MetricDaily`'de para birimi kolonu YOK — bu değer
  /// `AdAccount.currency` ile eşleşmeli, eşleşmiyorsa senkron uyarı yazar.
  currency: string;
  /**
   * Atıf penceresi başına dönüşüm ve gelir — Faz 6.
   *
   * ⚠️ `conversions`/`revenue` alanlarının YERİNE geçmez, yanına gelir:
   * yukarıdaki iki alan platformun KENDİ varsayılan penceresiyle sayılmış
   * sayıdır ve `metrics_daily`ye o yazılır. Buradaki satırlar
   * `metric_attribution_daily`ye gider.
   *
   * `undefined` = platform pencere kırılımı vermiyor (Google Ads: atıf ayarı
   * dönüşüm eyleminde, sorguda değil). `[]` ile aynı şey DEĞİL — boş dizi
   * "sordum, veri yok" demek olurdu ve o durumu bugün üreten bir yol yok.
   */
  attribution?: NormalizedInsightAttribution[];
  /**
   * E-ticaret hunisi — Faz 7.
   *
   * `undefined` = adapter huni okumuyor (bugün Google, TikTok). `null` alan =
   * adapter okudu, platform o ADIMI bildirmedi. İkisi de `metrics_daily`de
   * `NULL` kolona düşüyor ama ayrım burada korunuyor: `{purchases: null}`
   * dönen bir adapter "sordum, yok" diyor, `undefined` dönen "sormadım".
   *
   * ⚠️ Bu alanlar `conversions`ın yerine geçmiyor, yanına geliyor —
   * gerekçesi `analytics.ts` `FUNNEL_METRIC_KEYS` başlığında.
   */
  funnel?: NormalizedInsightFunnel;
  /**
   * Arama ağı gösterim payı — Genişleme Adım 10.
   *
   * `undefined` = adapter bu metriği hiç sormadı (Meta, LinkedIn, TikTok — o
   * platformlarda kavramın karşılığı YOK; Google'da ACCOUNT ve AD seviyeleri —
   * o kaynaklarda alan seçilemiyor). `null` alan = sordu, platform vermedi.
   *
   * `funnel`daki ayrımın aynısı ve aynı sonucu doğuruyor: `undefined`
   * kolonları upsert'e HİÇ sokmuyor (mevcut değer korunuyor), `null` onları
   * `NULL`a çekiyor.
   */
  impressionShare?: NormalizedInsightImpressionShare;
}

/**
 * Huni adımlarının bir günlük satırdaki değerleri.
 *
 * Alanlar `metrics_daily` kolonlarıyla BİREBİR aynı adı taşıyor: araya bir
 * çeviri sözlüğü koymak, adım eklendiğinde güncellenmeyi unutulacak üçüncü
 * bir yer üretirdi.
 */
export interface NormalizedInsightFunnel {
  addToCart: number | null;
  addToCartValue: number | null;
  initiateCheckout: number | null;
  initiateCheckoutValue: number | null;
  purchases: number | null;
  purchaseValue: number | null;
}

/**
 * Bir günlük satırın gösterim payı değerleri — hepsi 0..1 ORAN.
 *
 * Alan adları `metrics_daily` kolonlarıyla BİREBİR aynı (`funnel`daki kararla
 * aynı gerekçe: araya çeviri sözlüğü koymak, alan eklendiğinde güncellenmesi
 * unutulacak üçüncü bir yer üretirdi).
 */
export interface NormalizedInsightImpressionShare {
  searchImpressionShare: number | null;
  searchTopImpressionShare: number | null;
  searchAbsoluteTopImpressionShare: number | null;
  searchBudgetLostImpressionShare: number | null;
  searchRankLostImpressionShare: number | null;
}

/**
 * Gösterim payı DEĞER SEMANTİĞİNİN TEK YERİ — saf, platformdan bağımsız.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden burada, adapter'ın içinde değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu fonksiyonu iki yer çağırıyor: `apps/api/.../google.connector.ts` ve
 * `apps/worker/.../google-reader.ts`. İkisi ayrı derleme birimi ve aralarında
 * ikiz dosya sözleşmesi VAR (`twin-files.spec.ts`) ama bu mantık ikizlenecek
 * türden değil — `metric-mapping.ts` başlığındaki kural: "RİSKLİ MANTIK
 * ikizlenmez, yalnız GİRDİ/ÇIKIŞ tesisatı ikizlenir". Ortak pakete koymak,
 * iki kopyanın sessizce ayrışması ihtimalini tamamen ortadan kaldırıyor.
 *
 * Parametre adları PLATFORMDAN BAĞIMSIZ (`share`, `top`, `budgetLost`…):
 * Google'ın alan adlarını (`search_budget_lost_impression_share`) buraya
 * yazmak, adapter sınırının tek kuralını — "platforma özgü hiçbir kavram
 * dışarı çıkmaz" — bu dosyada delerdi. Çeviri çağıranın içinde kalıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 ARALIK DIŞI DEĞER `null` — kırpılmıyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Pay tanımı gereği 0..1. Dışarıda bir değer geldiyse anladığımız şey
 * bozulmuş demektir; 1'e kırpmak o bozukluğu geçerli bir sayı gibi
 * gösterirdi ve `impressionShareOverRange` onu paydaya alıp aralık payını
 * sessizce bozardı. `null` = "bilinmiyor" ve `SUM` onu atlıyor.
 *
 * Negatif değer de aynı sebeple `null`: uygun gösterim negatif olamaz.
 *
 * ⚠️ `0` GEÇERLİ ve korunuyor — "hiç çıkmadı" gerçek bir cevap ve tam da
 * kullanıcının görmesi gereken şey.
 */
export function normalizeImpressionShare(input: {
  share: unknown;
  top: unknown;
  absoluteTop: unknown;
  budgetLost: unknown;
  rankLost: unknown;
}): NormalizedInsightImpressionShare {
  return {
    searchImpressionShare: toShareRatio(input.share),
    searchTopImpressionShare: toShareRatio(input.top),
    searchAbsoluteTopImpressionShare: toShareRatio(input.absoluteTop),
    searchBudgetLostImpressionShare: toShareRatio(input.budgetLost),
    searchRankLostImpressionShare: toShareRatio(input.rankLost),
  };
}

/**
 * Tek bir pay değeri → 0..1 sayı ya da `null`.
 *
 * Dize kabul ediyor çünkü Google Ads istemcisi bazı sayısal alanları
 * `string` olarak veriyor (gRPC `int64`/`double` sarmalaması). ⚠️ `"< 0.1"`
 * gibi EŞİK METİNLERİ bu yolda GELMİYOR — alanların proto tipi `DOUBLE`
 * (kurulu `google-ads-api@24.1.0` alan tablosundan doğrulandı) ve kırpma
 * sayısal yapılıyor (%10 altı `0.0999`). Yine de dize gelirse `Number` onu
 * `NaN` yapıyor ve `NaN` `null`a düşüyor — uydurulmuş bir 0,05 yazılmıyor.
 */
export function toShareRatio(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return null;
  if (n < 0 || n > 1) return null;
  return n;
}

/// Tek bir pencerenin sayıları. `window` değerleri `ATTRIBUTION_WINDOWS`
/// (analytics.ts) ile aynı kümeden — tip oradan import edilmiyor çünkü bu
/// dosya connector sözleşmesi ve analytics'e bağımlı değil; sapma riski
/// `attribution-window.spec.ts` ile mıhlı.
export interface NormalizedInsightAttribution {
  window: string;
  conversions: number;
  revenue: number;
}

/**
 * Adapter'ın döndürdüğü normalize anahtar kelime (Faz 5).
 *
 * ---------------------------------------------------------------------------
 * Neden `parentPlatformRef` "ad set" diyor da "ad group" demiyor
 * ---------------------------------------------------------------------------
 * Google'ın "ad group"u bizim `AdSet`imiz — eşleme `meta.connector.ts`teki
 * `adset` eşlemesiyle aynı sınıftan bir çeviri ve adapter'ın İÇİNDE kalıyor.
 * Üst katmanlara "ad group" sözcüğünü sızdırmak, `analytics.sql.ts`'in
 * `ad_sets` tablosuna Google'a özgü ikinci bir yol açmak demekti.
 *
 * ---------------------------------------------------------------------------
 * 🔴 `matchType` NORMALİZE EDİLMİYOR — platform dizesi olduğu gibi taşınıyor
 * ---------------------------------------------------------------------------
 * Şema kararı (`Keyword.matchType`, Faz 1): `EXACT`/`PHRASE`/`BROAD` üç
 * platformda AYNI SÖZCÜKLE FARKLI ŞEY demek. Google'ın `PHRASE`i 2021'den
 * beri sıra değişikliğine izin veriyor, Microsoft'unki vermiyor. Ortak bir
 * enum'a sıkıştırmak, iki farklı eşleşme semantiğini tek isim altında
 * birleştirip kullanıcıya yanlış negatif kelime yazdırırdı.
 */
export interface NormalizedKeyword {
  /// Platformdaki kimlik. Google'da `criterion_id` — ebeveyni İÇİNDE tekil,
  /// hesap genelinde DEĞİL. Bu yüzden tekillik ebeveynle birlikte:
  /// `@@unique([adSetId, platformRef])` ya da `@@unique([campaignId, platformRef])`.
  platformRef: string;
  /// Kelimenin hangi seviyede yaşadığı.
  ///
  /// 🔴 `'campaign'` YALNIZ NEGATİF kelimelerde olabilir. Google'da kampanya
  /// seviyesinde pozitif kelime diye bir şey YOK — `campaign_criterion` bir
  /// hedefleme kısıtı, hedefin kendisi değil. Okuyucu bunu sorgusunda zaten
  /// filtreliyor; burada belirtiliyor ki üst katman "kampanya kelimesi neden
  /// hiç harcama görmüyor" diye aramasın.
  parentLevel: 'ad_set' | 'campaign';
  /// Ebeveynin platform kimliği — `parentLevel`e göre ad set'in (Google: ad
  /// group) ya da kampanyanın.
  parentPlatformRef: string;
  text: string;
  matchType: string;
  status: z.infer<typeof entityStatusSchema>;
  /// Negatif kelime mi. 🔴 Pozitif ve negatif kelimeler AYNI tabloda ve
  /// yalnız bu bayrakla ayrılıyor; filtresiz bir sorgu "en çok harcayan
  /// kelime" listesine hiç harcamamış negatifleri de karıştırır.
  negative: boolean;
  /// Hesabın para biriminde ANA birim. Kampanya otomatik teklif kullanıyorsa
  /// `null` — ⚠️ `0` DEĞİL: sıfır teklif "hiç teklif verme" demek olurdu ve
  /// `adjust_bid` onu yüzdeyle çarpıp sonsuza kadar sıfırda tutardı.
  cpcBid: number | null;
}

/**
 * Adapter'ın döndürdüğü normalize arama terimi (Faz 5).
 *
 * `NormalizedInsight`ten AYRI bir tip çünkü boyutu bir VARLIK değil bir METİN:
 * `platformRef`i yok, olamaz da — kullanıcının yazdığı serbest metin. Aynı
 * gerekçeyle `search_terms` da `metrics_daily`den ayrı bir tablo (Faz 1).
 */
export interface NormalizedSearchTerm {
  /// Üst ad set'in platform kimliği.
  parentPlatformRef: string;
  /// Hangi anahtar kelimeyle eşleşti. Platform söylemiyorsa `null` —
  /// "yakın varyant" eşleşmelerinde Google bunu vermeyebiliyor.
  keywordPlatformRef: string | null;
  text: string;
  /// `YYYY-MM-DD`, hesabın zaman diliminde.
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
}

/**
 * Adapter'ın döndürdüğü normalize lead — Faz 7.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Lead formu SAYFAYA bağlı, reklam hesabına DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu, senkron hattının bugün tanıdığı tek nesne grafiğinin (hesap →
 * kampanya → ad set → reklam) dışında bir gerçek. Meta'da form bir Facebook
 * SAYFASININ altında yaşıyor ve aynı form birden çok reklam hesabının
 * reklamlarında kullanılabiliyor.
 *
 * Adapter yine de reklam hiyerarşisinden okuyor (`/{campaign_id}/leads`)
 * çünkü:
 *   • Sayfa listesini gezmek AYRI bir izin ve ayrı bir grafik demek
 *     (`pages_show_list` + sayfa başına `leadgen_forms`), ve bağlı olduğumuz
 *     hesapla ilgisi olmayan formları da getirirdi.
 *   • Yükte `campaign_id`/`ad_id` zaten geliyor, yani hiyerarşiye bağlamak
 *     bedava.
 * Bedeli: forma bağlı AMA hiçbir reklamı olmayan lead görünmez. Doğru olan
 * bu — o lead bizim yönetmediğimiz bir harcamadan gelmiş demektir.
 *
 * ⚠️ Jeton `leads_retrieval` taşımak ZORUNDA (`META_LEADS_SCOPE`) ve
 * kullanıcının Sayfa üzerinde lead erişimi olmalı. İkisinden biri yoksa
 * Meta boş liste değil HATA dönüyor — ve bu iyi: sessiz boş liste "hiç lead
 * gelmedi" diye okunurdu.
 */
export interface NormalizedLead {
  /// Platformdaki lead kimliği. `Lead.platformRef`e gidiyor ve tekilliği
  /// bunun üstünden — aynı lead iki kez senkronlanırsa ikinci satır doğmasın.
  platformRef: string;
  /// `YYYY-MM-DDTHH:mm:ssZ` — formun DOLDURULDUĞU an, bizim okuduğumuz değil.
  submittedAt: string;
  /// Kampanya ve reklamın PLATFORM kimlikleri; senkron bunları bizim
  /// kimliklerimize çeviriyor. Platform vermezse `null` — bağlanamayan lead
  /// yine de kaydediliyor, atılmıyor (para o lead için harcandı).
  campaignPlatformRef: string | null;
  adPlatformRef: string | null;
  /**
   * Formun cevapları — `{ soru: cevap }`.
   *
   * 🔴 KİŞİSEL VERİ. Bu nesne `Lead.fields`e olduğu gibi yazılıyor ve
   * loglara, hata mesajlarına, denetim kaydına GİRMEMELİ. Meta cevapları
   * `[{name, values: []}]` biçiminde veriyor; düzleştirme adapter'ın içinde.
   */
  fields: Record<string, string>;
  /// Formun platform kimliği ve adı — hangi formdan geldiği. Şema donmuş
  /// olduğu için `Lead`de kolonları YOK; `fields` içine de karıştırılmıyor
  /// (orası kullanıcının cevapları). Bugün yalnız loglanıyor; kolon önerisi
  /// Faz 7 devir notunda.
  formPlatformRef: string | null;
  formName: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Uç sözleşmeleri
// ═══════════════════════════════════════════════════════════════════════════

/// `GET /integrations/platforms` — onboarding kartları ve bağlantı ekranı.
export interface PlatformCard {
  platform: AdPlatform;
  /// env anahtarları dolu mu. DEĞERLER ASLA DÖNMEZ, yalnız boolean.
  configured: boolean;
  /// Adapter yazıldı mı.
  implemented: boolean;
  /// Bu workspace'te kaç bağlantı var.
  connectionCount: number;
  capabilities: ConnectorCapabilities;
}

export const oauthStartSchema = z.object({
  /// OAuth bitince tarayıcının döneceği panel rotası. Açık yönlendirme
  /// (open redirect) olmasın diye YALNIZCA `/dashboard` ile başlayan göreli
  /// yollar kabul edilir; mutlak URL reddedilir.
  returnTo: z
    .string()
    .max(200)
    .regex(/^\/dashboard(\/[\w\-/[\]]*)?$/, 'Yalnızca panel içi göreli yol')
    .default('/dashboard/connections'),
});
export type OAuthStartInput = z.infer<typeof oauthStartSchema>;

export interface OAuthStartResponse {
  /// Tarayıcının gideceği platform izin ekranı adresi.
  authUrl: string;
  /// State'in son kullanma anı — arayüz "bağlantı penceresi zaman aşımına
  /// uğradı" diyebilsin.
  expiresAt: string;
}

/**
 * Geri dönüş ucu tarayıcıdan çağrılır ve HTML değil YÖNLENDİRME döner.
 * Hata hâlinde yığın izi değil tipli kod ile panele döner:
 *   `/dashboard/connections?error=OAUTH_DENIED`
 */
export const OAUTH_REDIRECT_ERROR = {
  OAUTH_DENIED: 'OAUTH_DENIED',
  OAUTH_STATE_EXPIRED: 'OAUTH_STATE_EXPIRED',
  OAUTH_STATE_INVALID: 'OAUTH_STATE_INVALID',
  OAUTH_EXCHANGE_FAILED: 'OAUTH_EXCHANGE_FAILED',
} as const;

/// Bağlantı satırı — BG1, BG3, BG6, BG9 aynı çağrıdan beslenir.
export interface ConnectionRow {
  id: string;
  platform: AdPlatform;
  displayName: string | null;
  status: ConnectionStatus;
  /// Verilen izin kapsamları (GV7 ekranı bunu basar).
  scopes: string[];
  /// Jetonun bitiş anı. JETONUN KENDİSİ ASLA DÖNMEZ.
  tokenExpiresAt: string | null;
  lastSyncedAt: string | null;
  /// Temizlenmiş hata metni; jeton parçası içermez.
  lastError: string | null;
  connectedAt: string;
  accountCount: number;
  /// Bu bağlantıya bağlı hesaplardan senkronu açık olanların sayısı.
  syncEnabledCount: number;
}

export interface AdAccountRow {
  id: string;
  connectionId: string;
  platform: AdPlatform;
  platformRef: string;
  name: string;
  currency: string;
  timezone: string;
  status: string;
  syncEnabled: boolean;
  lastSyncedAt: string | null;
  /// Son senkron denemesinin sonucu — BG6.
  lastSyncResult: 'ok' | 'failed' | 'never' | 'running';
  /// Üst üste kaçıncı hata (sağlık eşiğinin girdisi).
  consecutiveFailures: number;
}

/// `GET /integrations/connections` — tek çağrı, dokuz özellik.
export interface ConnectionsResponse {
  connections: ConnectionRow[];
  accounts: AdAccountRow[];
  /// BG9: "10 hesaptan 7'si bağlı". `limit === -1` sınırsız.
  planUsage: { used: number; limit: number; remaining: number };
  /// GB8 uyarı şeridi bunu okur.
  healthSummary: { healthy: number; degraded: number; disconnected: number };
}

export const discoveryQuerySchema = z.object({
  /// Önbelleği atla — kullanıcı "yenile" dediğinde.
  refresh: boolParam.optional(),
});

export interface DiscoveryResponse {
  connectionId: string;
  platform: AdPlatform;
  accounts: DiscoveredAccount[];
  /// HK4: kaç hesap daha bağlanabilir. `-1` sınırsız.
  planRemaining: number;
  /// Önbellekten mi geldi, ne zaman üretildi.
  cachedAt: string;
  /**
   * §11: Advanced Access gelene kadar uygulama yalnızca uygulamada ROLÜ OLAN
   * kullanıcıların hesaplarını görebiliyor. Liste boşsa bu bayrak `true`
   * döner ve arayüz "hesabınız yok" gibi YANILTICI bir boş durum göstermez.
   */
  devModeLimited: boolean;
}

export const connectAccountsSchema = z.object({
  /// Seçilen hesapların platform kimlikleri.
  platformRefs: z.array(z.string().min(1).max(120)).min(1).max(100),
});
export type ConnectAccountsInput = z.infer<typeof connectAccountsSchema>;

/**
 * Plan limiti aşılırsa TAMAMI reddedilir (`AD_ACCOUNT_LIMIT`), kısmi bağlama
 * YAPILMAZ. Kısmi bağlarsak kullanıcı hangisinin bağlandığını tahmin etmek
 * zorunda kalır; hepsini reddetmek can sıkıcı ama anlaşılır.
 */
export interface ConnectAccountsResponse {
  connected: AdAccountRow[];
  /// İlk senkron işlerinin kimlikleri — HK5 ilerleme ekranı bunları izler.
  syncJobIds: string[];
}

/// BG5 — kaldırmadan önce etkiyi göster. İki aşamalı olması bilinçli:
/// cascade geri dönüşsüz ve GEÇMİŞ METRİKLERİ DE siliyor.
export interface DisconnectImpact {
  accountCount: number;
  campaignCount: number;
  metricRowCount: number;
  /// Kapsamı bu hesaplara dokunan kurallar — durdurulacaklar.
  affectedWorkflows: { id: string; name: string; enabled: boolean }[];
  /// Bu bağlantıdan gelen kreatiflerin yayınlandığı reklam sayısı.
  publishedCreativeCount: number;
  /// En eski metrik tarihi — "8 aylık geçmiş silinecek" diyebilmek için.
  oldestMetricDate: string | null;
}

export const disconnectSchema = z.object({
  /// Çift onay: bağlantının görünen adı birebir yazılmalı.
  confirmName: z.string().min(1).max(200),
  /// Platformda jetonu da iptal etmeyi dene. Başarısız olursa silme yine de
  /// sürer — bizim tarafımızda kayıt kalmamalı.
  revokeOnPlatform: z.boolean().default(true),
});
export type DisconnectInput = z.infer<typeof disconnectSchema>;

export const syncTriggerSchema = z.object({
  /// Kaç günlük geriye dönük düzeltme istensin. Varsayılan: bugün + dün.
  days: z.number().int().min(1).max(90).default(2),
});
export type SyncTriggerInput = z.infer<typeof syncTriggerSchema>;

export interface SyncJobResponse {
  jobId: string;
  accountId: string;
  queuedAt: string;
  /// Sıklık sınırına takıldıysa 429 döner; bu alan yanıtta değil hata
  /// gövdesinin `details`'inde gelir.
  estimatedSeconds: number | null;
}

/// BG8 — senkron hata detayı. §9: sessizce yutulmaz.
export interface SyncErrorRow {
  jobId: string | null;
  accountId: string;
  failedAt: string;
  attempts: number;
  /// Bizim sınıflandırmamız: hangi kova (jeton / kota / geçici / veri).
  category: 'auth' | 'rate_limit' | 'transient' | 'data' | 'unknown';
  /// Platformun ham hata gövdesi — JETON ALANLARI TEMİZLENMİŞ.
  providerError: string;
  /// Dead-letter'a düştü mü (yeniden deneme hakkı bitti).
  deadLettered: boolean;
}

export const syncErrorsQuerySchema = listQuerySchema.extend({
  accountId: z.uuid().optional(),
});

/// Senkron durumu — OB3 bekleme ekranı ve KB4 tazelik etiketi.
export interface SyncStatusResponse {
  accounts: {
    accountId: string;
    name: string;
    state: 'never' | 'running' | 'ok' | 'failed';
    progress: number | null;
    lastSyncedAt: string | null;
    lastError: string | null;
  }[];
  /// Hepsi en az bir kez senkronlandı mı — onboarding adımı bunu bekliyor.
  initialSyncComplete: boolean;
}

export const connectionListQuerySchema = z.object({
  platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN']).optional(),
  status: connectionStatusSchema.optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// Admin — platform yönetimi (salt okunur + tek yıkıcı işlem)
// ═══════════════════════════════════════════════════════════════════════════

/// `GET /admin/platforms` — env DEĞERLERİ ASLA DÖNMEZ, yalnız "dolu mu".
export interface AdminPlatformRow {
  platform: AdPlatform;
  configured: boolean;
  implemented: boolean;
  /// Hangi env anahtarları eksik — destek "neden çalışmıyor" sorusuna
  /// anahtarın DEĞERİNİ görmeden cevap verebilsin.
  missingEnvKeys: string[];
  /// Tüm kiracılardaki bağlantı sağlığı özeti (kiracı sınırı BİLEREK aşılıyor).
  health: { healthy: number; degraded: number; disconnected: number };
  connectionCount: number;
  accountCount: number;
  /// En eski "kopuk" bağlantı ne zamandır kopuk — destek önceliklendirsin.
  oldestDisconnectedAt: string | null;
}

/**
 * Platform aç/kapa BAYRAĞI BU TURDA YOK.
 *
 * Bayrağı saklayacak bir tablo şemada yok (`PlatformSetting` önerisi
 * `semaDegisikligi`'nde). Redis'e yazmak çözüm değil: denetim kaydı ve
 * kalıcılık kaybolur, ki bir platformu tüm kiracılara kapatan düğmenin
 * gerektirdiği tam da bu ikisi. Ekran bugün SALT OKUNUR.
 */
export const PLATFORM_TOGGLE_AVAILABLE = false;

/// `GET /admin/connections` — tüm kiracılardaki bağlantılar, destek görünümü.
export const adminConnectionListQuerySchema = listQuerySchema.extend({
  platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN']).optional(),
  status: connectionStatusSchema.optional(),
  workspaceId: z.uuid().optional(),
  sort: z.enum(['createdAt', 'lastSyncedAt', 'status']).default('lastSyncedAt'),
  dir: z.enum(['asc', 'desc']).default('asc'),
});

export interface AdminConnectionRow extends ConnectionRow {
  workspaceId: string;
  workspaceName: string;
}

/// Zorla kesme — en yıkıcı integrations işlemi. Gerekçe zorunlu, audit şart.
export const adminForceDisconnectSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  /// Jeton kasasındaki şifreli değerleri de sil. Varsayılan `true`:
  /// bozuk bağlantının jetonunu tutmanın bir faydası yok.
  purgeTokens: z.boolean().default(true),
});
export type AdminForceDisconnectInput = z.infer<typeof adminForceDisconnectSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

export const IntegrationErrorCode = {
  /// Adapter yazılmadı (GOOGLE / TIKTOK). HTTP 501 değil 400 — istemci
  /// hatası değil ama beklenen bir durum; arayüz "yakında" gösterir.
  PLATFORM_NOT_AVAILABLE: 'PLATFORM_NOT_AVAILABLE',
  /// env anahtarları eksik.
  PLATFORM_NOT_CONFIGURED: 'PLATFORM_NOT_CONFIGURED',
  CONNECTION_NOT_FOUND: 'CONNECTION_NOT_FOUND',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  /// `Subscription.maxAdAccounts` aşılıyor. `details: {limit, used, requested}`.
  AD_ACCOUNT_LIMIT: 'AD_ACCOUNT_LIMIT',
  /// OAuth state süresi doldu ya da eşleşmedi.
  OAUTH_STATE_INVALID: 'OAUTH_STATE_INVALID',
  /// Kullanıcı izni reddetti.
  OAUTH_DENIED: 'OAUTH_DENIED',
  /// Jeton yenilenemedi — bağlantı DISCONNECTED işaretlendi.
  TOKEN_REFRESH_FAILED: 'TOKEN_REFRESH_FAILED',
  /// Elle senkron sıklık sınırı. `details.retryAfterSeconds`.
  SYNC_TOO_SOON: 'SYNC_TOO_SOON',
  /// Bu hesabın senkronu zaten sürüyor.
  SYNC_IN_PROGRESS: 'SYNC_IN_PROGRESS',
  /**
   * Demo hesap senkronlanamaz (Faz 8).
   *
   * Demo bağlantının jetonu şifreli metin değil bir işaret; platforma
   * gidilemez. 400 — istek geçerli ama bu hesapta anlamı yok. Arayüz
   * "yenile" düğmesini demo hesapta hiç göstermemeli; bu hata o düğmenin
   * yanlışlıkla açık kalması hâlinde anlaşılır bir cevap veriyor.
   */
  DEMO_ACCOUNT_NOT_SYNCABLE: 'DEMO_ACCOUNT_NOT_SYNCABLE',
  /**
   * Demo BAĞLANTI platforma hiç gidemez — senkrondan geniş.
   *
   * `DEMO_ACCOUNT_NOT_SYNCABLE` yalnız senkron ucunu koruyordu
   * (`sync-dispatch.service.ts`). Ama demo bağlantının jetonu sahte bir
   * işaret, yani kampanya oluşturma, kreatif yayınlama, kimlik listeleme —
   * platforma giden HER yol aynı duvara çarpıyordu. Çarpma biçimi yanlış
   * teşhisti: kasa sahte metni çözemediği için `TOKEN_REFRESH_FAILED`
   * dönüyordu, yani kullanıcıya "bağlantın bozuldu, yeniden bağlan" deniyordu.
   * Yeniden bağlanmak demo bağlantıda mümkün değil ve gereken de o değil.
   *
   * Kapı artık `ConnectionAccessService.open()` içinde: tek giriş noktası
   * olduğu için sonradan eklenen her uç bu korumayı kendiliğinden miras
   * alıyor.
   */
  DEMO_CONNECTION_NOT_CALLABLE: 'DEMO_CONNECTION_NOT_CALLABLE',
  /// Silme onayında yazılan ad eşleşmedi.
  CONFIRMATION_MISMATCH: 'CONFIRMATION_MISMATCH',
  /// Meta geliştirme kipi: uygulamada rolü olmayan kullanıcının hesapları
  /// görünmüyor (§11). "Hesabınız yok" DEĞİL.
  META_DEV_MODE_LIMITED: 'META_DEV_MODE_LIMITED',
  /// Platform kotası doldu; iş yeniden kuyruğa alındı.
  PROVIDER_RATE_LIMITED: 'PROVIDER_RATE_LIMITED',
  /**
   * Platformda anahtar kelime kavramı YOK (Faz 5) — Meta ve OpenAI Ads.
   *
   * `PLATFORM_NOT_AVAILABLE`den ayrı tutulmasının sebebi, ikisinin kullanıcıya
   * söylediği şeyin taban tabana zıt olması: birincisi "bekleyin, yazılacak",
   * bu ise "beklemeyin, olmayacak". Aynı koda düşselerdi arayüz Meta hesabına
   * kelime kuralı kuran kullanıcıya "yakında" derdi ve o gün hiç gelmezdi.
   */
  PLATFORM_HAS_NO_KEYWORDS: 'PLATFORM_HAS_NO_KEYWORDS',
  /**
   * Google Ads geliştirici jetonu **test hesabı** erişimiyle sınırlı (§11).
   *
   * Meta'nın geliştirme kipiyle aynı sınıf hata ve ayrı kodu hak ediyor:
   * jeton geçerli, OAuth başarılı, hesap listesi BOŞ dönüyor. "Hesabınız yok"
   * demek kullanıcıyı Google konsolunda olmayan bir hesabı aramaya gönderir.
   */
  GOOGLE_TEST_ACCOUNT_ONLY: 'GOOGLE_TEST_ACCOUNT_ONLY',
  /**
   * Lead okuma bu adapter'da HENÜZ yazılmadı (Faz 7) — Google, TikTok.
   *
   * `PLATFORM_HAS_NO_KEYWORDS`in TERSİ bir durum ve o yüzden ayrı kod:
   * kelime Meta'da hiç olmayacak, lead formu ise Google'da VAR ve bir gün
   * okunacak. Aynı koda düşselerdi arayüz Google kullanıcısına "bu
   * platformda lead yok" derdi — düpedüz yanlış bir cümle.
   *
   * `PLATFORM_NOT_AVAILABLE` de olmaz: Google adapter'ı yazıldı ve
   * çalışıyor, eksik olan tek bir yetenek.
   */
  LEADS_NOT_IMPLEMENTED: 'LEADS_NOT_IMPLEMENTED',
  /**
   * LinkedIn **Development tier**: hesap listesi boş dönebiliyor (Genişleme
   * Adım 5).
   *
   * Meta'nın geliştirme kipi ve Google'ın test jetonuyla AYNI SINIF hata —
   * OAuth başarılı, jeton geçerli, liste boş ve hiçbir yerde hata yok. Ama
   * çözümü ikisinden de farklı: uygulama incelemesi ya da jeton başvurusu
   * değil, hesabın **Developer Portal > Advertising API > Ad Accounts**
   * sekmesine 9 haneli kimliğiyle ELLE eklenmesi. Aynı koda düşselerdi arayüz
   * LinkedIn kullanıcısını yanlış sayfaya gönderirdi.
   *
   * ⚠️ Bu kısıtın okuma tarafını da kapsayıp kapsamadığı LinkedIn'in kendi
   * dokümanında ÇELİŞKİLİ (bir sayfa "okumada sınır yok" diyor, diğeri "yalnız
   * portala eklenen hesaplar yönetilebilir"). Metin bu belirsizliği
   * gizlememeli.
   */
  LINKEDIN_DEV_TIER_LIMITED: 'LINKEDIN_DEV_TIER_LIMITED',
  /**
   * TikTok yetkilendirmesi başarılı ama HİÇBİR reklam hesabı gelmedi
   * (Genişleme Adım 4).
   *
   * Meta/Google/LinkedIn üçlüsüyle aynı SINIF hata — OAuth başarılı, jeton
   * geçerli, liste boş ve hiçbir yerde hata yok — ama ÇÖZÜMÜ üçünden de
   * farklı ve ayrı bir kodu bu yüzden hak ediyor: orada sorun BİZDE (uygulama
   * incelemesi / jeton başvurusu / hesabın portala elle eklenmesi), burada
   * KULLANICIDA. TikTok hesabı var ama Ads Manager ya da Business Center'da
   * reklam hesabı yok, ya da izin ekranında hesap SEÇİLMEMİŞ (TikTok
   * hesapları tek tek işaretletiyor).
   *
   * `ACCOUNT_NOT_FOUND` de olmaz: hesap "bulunamadı" değil, YETKİLENDİRİLMEDİ
   * — kullanıcı onu TikTok'ta gözüyle görüyor. Aynı koda düşselerdi arayüz
   * TikTok kullanıcısını "bekleyin, biz başvurduk" cümlesine gönderirdi;
   * oysa yapabileceği bir şey var.
   */
  TIKTOK_NO_ADVERTISER_GRANTED: 'TIKTOK_NO_ADVERTISER_GRANTED',
  /**
   * Anahtar kelime bu adapter'da HENÜZ yazılmadı (Genişleme Adım 6) — X.
   *
   * 🔴 `PLATFORM_HAS_NO_KEYWORDS` ile KARIŞTIRILMAYACAK; ikisi taban tabana
   * zıt cümle söylüyor ve bu kod tam olarak o karışıklığı önlemek için var.
   * X'te anahtar kelime hedefleme **VAR** (`targeting_criteria` kaynağında
   * `BROAD_KEYWORD` / `PHRASE_KEYWORD` / `EXACT_KEYWORD` / `NEGATIVE_*`
   * ailesi) — biz okumadık. `PLATFORM_HAS_NO_KEYWORDS` dönseydi arayüz
   * "X'te anahtar kelime yok" derdi ve bu düpedüz yanlış olurdu; üstelik
   * otomasyon motoru o kuralı KALICI olarak `SKIPPED` sayardı, oysa burada
   * kapatılacak bir borç var.
   *
   * `LEADS_NOT_IMPLEMENTED` ile AYNI SINIF: "adapter var, bu yetenek henüz
   * yazılmadı". Ayrı kod olmasının sebebi arayüzün iki eksiği ayrı ayrı
   * sayabilmesi — biri kapandığında diğeri hâlâ açık kalabiliyor.
   */
  KEYWORDS_NOT_IMPLEMENTED: 'KEYWORDS_NOT_IMPLEMENTED',
} as const;
export type IntegrationErrorCode = (typeof IntegrationErrorCode)[keyof typeof IntegrationErrorCode];
